import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, ArrowLeft, FileText, Wallet, ArrowUpCircle, ArrowDownCircle, TrendingUp, Banknote, CalendarClock, Trash2, Pencil, KeyRound, History, Lock, Unlock, Scale } from "lucide-react";
import { PortalAccessManager } from "@/components/PortalAccess";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, Legend, CartesianGrid } from "recharts";
import adminApi from "@/lib/adminApi";

const money = (n) => (n == null ? "—" : `$${Number(n).toLocaleString("es", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const pct = (n) => (n == null ? "—" : `${Number(n)}%`);
const fdate = (s) => { if (!s) return "—"; try { const hasTime = typeof s === "string" && s.length > 10; const d = new Date(hasTime ? s : s + "T00:00:00"); const b = `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`; return hasTime ? `${b} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}` : b; } catch { return s; } };
const today = () => new Date().toISOString().slice(0, 10);
const num = (v) => { if (v === "" || v == null) return null; const n = Number(String(v).replace(",", ".")); return Number.isFinite(n) ? n : null; };
const MONTHS_ES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const STATUS_META = {
  PAGADO: { label: "Pagado", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  PARCIAL: { label: "Parcial", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  PENDIENTE: { label: "Pendiente", cls: "bg-rose-50 text-rose-700 border-rose-200" },
  SIN_INTERES: { label: "—", cls: "bg-zinc-50 text-zinc-400 border-zinc-200" },
};

const TYPE_META = {
  APORTE_CAPITAL: { label: "Aporte de capital", color: "text-emerald-600", bg: "bg-emerald-50 border-emerald-200", Icon: ArrowUpCircle },
  RETIRO_CAPITAL: { label: "Retiro de capital", color: "text-rose-600", bg: "bg-rose-50 border-rose-200", Icon: ArrowDownCircle },
  INTERES_GENERADO: { label: "Interés generado", color: "text-blue-600", bg: "bg-blue-50 border-blue-200", Icon: TrendingUp },
  PAGO_INTERES: { label: "Pago de interés", color: "text-violet-600", bg: "bg-violet-50 border-violet-200", Icon: Banknote },
};
const METHODS = ["Efectivo", "Zelle", "USDT", "Transferencia", "Otro"];
const fileToData = (file) => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });
const openReceiptAdmin = async (id) => {
  const w = window.open("", "_blank");
  try {
    const r = await adminApi.get(`/admin/ledger/${id}/receipt`);
    const data = r.data.receipt;
    if (!data) { if (w) w.close(); toast.error("Sin comprobante"); return; }
    const isData = typeof data === "string" && data.startsWith("data:");
    const url = isData ? URL.createObjectURL(await (await fetch(data)).blob()) : data;
    if (w) w.location.href = url; else window.open(url, "_blank");
    if (isData) setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch { if (w) w.close(); toast.error("Sin comprobante"); }
};

function Stat({ label, value, accent, testid }) {
  return <div className={`rounded-xl border p-3 ${accent ? "bg-emerald-50 border-emerald-200" : "bg-white border-zinc-200"}`} data-testid={testid}><p className="text-[10px] uppercase tracking-wider text-zinc-400">{label}</p><p className={`text-lg font-bold mt-0.5 ${accent ? "text-emerald-700" : "text-zinc-900"}`}>{value}</p></div>;
}

// ----------------------------- Registrar movimiento -----------------------------
function AddMovementForm({ investorId, onDone }) {
  const [f, setF] = useState({ type: "APORTE_CAPITAL", amount: "", effective_date: today(), period: "", method: "", reference: "", note: "", capital_base: "", rate_pct: "", days: "", import_key: "" });
  const [receipt, setReceipt] = useState(null);
  const [force, setForce] = useState(false);
  const [saving, setSaving] = useState(false);
  const [allocs, setAllocs] = useState([]);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const isInterest = f.type === "INTERES_GENERADO";
  const isPayment = f.type === "PAGO_INTERES";
  const addAlloc = () => setAllocs((a) => [...a, { period: "", amount: "" }]);
  const setAlloc = (i, k, v) => setAllocs((a) => a.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const rmAlloc = (i) => setAllocs((a) => a.filter((_, idx) => idx !== i));
  const allocSum = allocs.reduce((s, x) => s + (num(x.amount) || 0), 0);
  const submit = async () => {
    if (num(f.amount) == null || num(f.amount) <= 0) { toast.error("Monto > 0 requerido"); return; }
    if (isInterest && !f.period) { toast.error("Período requerido para interés generado"); return; }
    let allocations = null;
    if (isPayment && allocs.length) {
      const clean = allocs.filter((x) => x.period && num(x.amount) != null);
      if (clean.length) {
        const sum = clean.reduce((s, x) => s + num(x.amount), 0);
        if (Math.abs(sum - num(f.amount)) > 0.01) { toast.error("La suma de las asignaciones debe igualar el monto del pago"); return; }
        allocations = clean.map((x) => ({ period: x.period, amount: num(x.amount) }));
      }
    }
    setSaving(true);
    try {
      await adminApi.post(`/admin/investors/${investorId}/ledger`, {
        type: f.type, amount: num(f.amount), effective_date: f.effective_date,
        period: f.period || null, method: f.method || null, reference: f.reference || null,
        note: f.note || null, receipt, import_key: f.import_key || null, force,
        capital_base: num(f.capital_base),
        rate_pct: num(f.rate_pct),
        days: num(f.days),
        allocations,
      });
      toast.success("Movimiento registrado");
      setF({ ...f, amount: "", reference: "", note: "", import_key: "", capital_base: "", rate_pct: "", days: "" });
      setReceipt(null); setForce(false); setAllocs([]);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-4 space-y-3" data-testid="adm-ledger-form">
      <p className="text-sm font-semibold">Registrar movimiento</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div className="col-span-2 sm:col-span-1">
          <label className="text-[11px] text-zinc-500">Tipo</label>
          <select data-testid="adm-ledger-type" value={f.type} onChange={(e) => set("type", e.target.value)} className="h-10 w-full rounded-lg border border-zinc-200 text-sm px-2 bg-white">
            {Object.entries(TYPE_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
        <div><label className="text-[11px] text-zinc-500">Monto</label><Input data-testid="adm-ledger-amount" type="text" inputMode="decimal" value={f.amount} onChange={(e) => set("amount", e.target.value)} className="h-10 rounded-lg" placeholder="0,00" /></div>
        <div><label className="text-[11px] text-zinc-500">Fecha efectiva</label><Input data-testid="adm-ledger-date" type="date" value={f.effective_date} onChange={(e) => set("effective_date", e.target.value)} className="h-10 rounded-lg" /></div>
        <div><label className="text-[11px] text-zinc-500">Período {isInterest ? "*" : "(opc.)"}</label><Input data-testid="adm-ledger-period" placeholder="2026-02" value={f.period} onChange={(e) => set("period", e.target.value)} className="h-10 rounded-lg" /></div>
        <div>
          <label className="text-[11px] text-zinc-500">Método</label>
          <select data-testid="adm-ledger-method" value={f.method} onChange={(e) => set("method", e.target.value)} className="h-10 w-full rounded-lg border border-zinc-200 text-sm px-2 bg-white">
            <option value="">—</option>{METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        <div><label className="text-[11px] text-zinc-500">Referencia</label><Input data-testid="adm-ledger-reference" value={f.reference} onChange={(e) => set("reference", e.target.value)} className="h-10 rounded-lg" /></div>
        <div className="col-span-2"><label className="text-[11px] text-zinc-500">Nota</label><Input data-testid="adm-ledger-note" value={f.note} onChange={(e) => set("note", e.target.value)} className="h-10 rounded-lg" /></div>
      </div>
      {isInterest && (
        <div className="grid grid-cols-3 gap-2 rounded-xl bg-blue-50/60 border border-blue-100 p-2">
          <div><label className="text-[11px] text-zinc-500">Capital base</label><Input data-testid="adm-ledger-capital-base" type="text" inputMode="decimal" value={f.capital_base} onChange={(e) => set("capital_base", e.target.value)} className="h-9 rounded-lg text-sm" placeholder="0,00" /></div>
          <div><label className="text-[11px] text-zinc-500">Tasa %</label><Input data-testid="adm-ledger-rate" type="text" inputMode="decimal" value={f.rate_pct} onChange={(e) => set("rate_pct", e.target.value)} className="h-9 rounded-lg text-sm" placeholder="1,5" /></div>
          <div><label className="text-[11px] text-zinc-500">Días (prorrateo)</label><Input data-testid="adm-ledger-days" type="text" inputMode="numeric" value={f.days} onChange={(e) => set("days", e.target.value)} className="h-9 rounded-lg text-sm" /></div>
        </div>
      )}
      {isPayment && (
        <div className="rounded-xl bg-violet-50/60 border border-violet-100 p-2.5 space-y-2" data-testid="adm-alloc-block">
          <div className="flex items-center justify-between">
            <p className="text-[11px] text-zinc-600 font-medium">Repartir el pago en varios períodos (opcional)</p>
            <button type="button" data-testid="adm-alloc-add" onClick={addAlloc} className="text-[11px] text-violet-700 inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Agregar período</button>
          </div>
          {allocs.map((x, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`adm-alloc-row-${i}`}>
              <Input data-testid={`adm-alloc-period-${i}`} placeholder="2026-02" value={x.period} onChange={(e) => setAlloc(i, "period", e.target.value)} className="h-9 rounded-lg text-sm flex-1" />
              <Input data-testid={`adm-alloc-amount-${i}`} type="text" inputMode="decimal" placeholder="0,00" value={x.amount} onChange={(e) => setAlloc(i, "amount", e.target.value)} className="h-9 rounded-lg text-sm w-28" />
              <button type="button" data-testid={`adm-alloc-del-${i}`} onClick={() => rmAlloc(i)} className="text-zinc-400 hover:text-rose-600"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
          {allocs.length > 0 && (
            <p className={`text-[11px] ${Math.abs(allocSum - (num(f.amount) || 0)) > 0.01 ? "text-rose-600" : "text-emerald-600"}`} data-testid="adm-alloc-sum">Σ asignado {money(allocSum)} / monto {money(num(f.amount))}</p>
          )}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-[11px] text-zinc-500 flex items-center gap-1.5">Comprobante (opc.)
          <input type="file" accept="image/*,application/pdf" data-testid="adm-ledger-receipt" onChange={async (e) => { const file = e.target.files?.[0]; if (file) setReceipt(await fileToData(file)); }} className="text-xs" />
        </label>
        <Input data-testid="adm-ledger-importkey" placeholder="import_key (opc.)" value={f.import_key} onChange={(e) => set("import_key", e.target.value)} className="h-9 rounded-lg text-xs w-44" />
        {f.type === "RETIRO_CAPITAL" && (
          <label className="text-[11px] text-rose-600 flex items-center gap-1.5"><input type="checkbox" data-testid="adm-ledger-force" checked={force} onChange={(e) => setForce(e.target.checked)} /> Forzar (capital negativo)</label>
        )}
        <Button data-testid="adm-ledger-submit" onClick={submit} disabled={saving} className="h-10 rounded-lg bg-zinc-900 hover:bg-zinc-800 ml-auto">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Registrar"}</Button>
      </div>
    </div>
  );
}

// ----------------------------- Generar rendimiento -----------------------------
function GenerateInterest({ investorId, nextYield, onDone }) {
  const [f, setF] = useState({ period: nextYield?.period || "", capital_base: "", rate_pct: "", days: "", effective_date: "" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const gen = async () => {
    if (!f.period) { toast.error("Período requerido"); return; }
    setSaving(true);
    try {
      const r = await adminApi.post(`/admin/investors/${investorId}/generate-interest`, {
        period: f.period, capital_base: num(f.capital_base),
        rate_pct: num(f.rate_pct), days: num(f.days),
        effective_date: f.effective_date || null,
      });
      if (r.data.grace_zero) toast.info("Mes de gracia (0%) — no se registró rendimiento.");
      else if (r.data.generated) toast.success(`Rendimiento ${money(r.data.amount)} generado para ${f.period}`);
      else toast.info(r.data.message || "Sin rendimiento");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-4 space-y-3" data-testid="adm-generate-interest">
      <p className="text-sm font-semibold">Generar rendimiento del período</p>
      <p className="text-[11px] text-zinc-400 -mt-1">"Días prorrateo" ajusta el interés por una fracción del mes; NO es el día de generación mensual (eso se configura en Resumen).</p>
      {nextYield && (
        <p className="text-xs text-zinc-500" data-testid="adm-next-yield-hint">
          Próximo período sugerido: <b>{nextYield.period}</b>{nextYield.grace_zero ? ` (mes de gracia ${nextYield.candidate_period} = 0%, se omite)` : ""} · capital base {money(nextYield.capital_base)} × {pct(nextYield.rate_pct)} ≈ <b>{money(nextYield.projected_interest)}</b>
        </p>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        <div><label className="text-[11px] text-zinc-500">Período</label><Input data-testid="adm-gen-period" placeholder="2026-02" value={f.period} onChange={(e) => set("period", e.target.value)} className="h-10 rounded-lg" /></div>
        <div><label className="text-[11px] text-zinc-500">Capital base (opc.)</label><Input data-testid="adm-gen-capital" type="text" inputMode="decimal" value={f.capital_base} onChange={(e) => set("capital_base", e.target.value)} className="h-10 rounded-lg" placeholder="0,00" /></div>
        <div><label className="text-[11px] text-zinc-500">Tasa % (opc.)</label><Input data-testid="adm-gen-rate" type="text" inputMode="decimal" value={f.rate_pct} onChange={(e) => set("rate_pct", e.target.value)} className="h-10 rounded-lg" placeholder="1,5" /></div>
        <div><label className="text-[11px] text-zinc-500">Días prorrateo (opc.)</label><Input data-testid="adm-gen-days" type="text" inputMode="numeric" value={f.days} onChange={(e) => set("days", e.target.value)} className="h-10 rounded-lg" /></div>
        <div className="flex items-end"><Button data-testid="adm-gen-submit" onClick={gen} disabled={saving} className="h-10 w-full rounded-lg bg-blue-600 hover:bg-blue-700">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Generar"}</Button></div>
      </div>
    </div>
  );
}

// ----------------------------- Tasas -----------------------------
function CalendarEditor({ investorId, onDone }) {
  const MONTHS = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
  const nowY = new Date().getFullYear();
  const YEARS = []; for (let y = nowY - 2; y <= nowY + 2; y++) YEARS.push(String(y));
  const [year, setYear] = useState(String(nowY));
  const [rows, setRows] = useState(null);
  const [graceMonth, setGraceMonth] = useState(1);
  const [saving, setSaving] = useState(false);
  const applyCal = (data) => { setRows(data.months.map((m) => ({ rate: m.rate_pct == null ? "" : String(m.rate_pct) }))); setGraceMonth(data.grace_month); };
  const loadYear = (y) => adminApi.get(`/admin/investors/${investorId}/calendar?year=${y}`).then((r) => applyCal(r.data)).catch(() => toast.error("No se pudo cargar el calendario"));
  useEffect(() => { loadYear(year); /* eslint-disable-next-line */ }, [investorId]);
  const changeYear = (y) => { setYear(y); setRows(null); loadYear(y); };
  const setRate = (i, v) => setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, rate: v } : r)));
  const copyPrev = async () => {
    try { const r = await adminApi.get(`/admin/investors/${investorId}/calendar?year=${Number(year) - 1}`); applyCal(r.data); toast.info(`Tasas de ${Number(year) - 1} copiadas (borrador, recuerda Guardar).`); }
    catch { toast.error("No se pudo copiar"); }
  };
  const save = async () => {
    setSaving(true);
    try { await adminApi.put(`/admin/investors/${investorId}/calendar`, { year, grace_month: graceMonth, months: rows.map((r, i) => ({ month: i + 1, rate_pct: num(r.rate) })) }); toast.success(`Calendario ${year} guardado`); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-4 space-y-3" data-testid="adm-calendar">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-sm font-semibold">Calendario de rendimientos</p>
        <div className="flex items-center gap-1.5"><span className="text-xs text-zinc-500">Año</span>
          <select data-testid="adm-cal-year" value={year} onChange={(e) => changeYear(e.target.value)} className="h-9 rounded-lg border border-zinc-200 text-sm px-2 bg-white">
            {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>
      <p className="text-[11px] text-zinc-500">Define la tasa mensual (acepta decimales) y marca el único mes de gracia del año (0%). Guardar no recalcula rendimientos históricos ya generados.</p>
      {!rows ? <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-zinc-400" /></div> : (
        <div className="rounded-xl border border-zinc-100 divide-y divide-zinc-100">
          <div className="grid grid-cols-[1fr_auto_auto] gap-2 px-3 py-1.5 text-[10px] uppercase tracking-wider text-zinc-400"><span>Mes</span><span className="w-24 text-right">Tasa %</span><span className="w-28 text-center">Mes de gracia</span></div>
          {rows.map((r, i) => {
            const isGrace = graceMonth === i + 1;
            return (
              <div key={i} className="grid grid-cols-[1fr_auto_auto] gap-2 px-3 py-1.5 items-center" data-testid={`adm-cal-row-${i + 1}`}>
                <span className="text-sm text-zinc-700">{MONTHS[i]}</span>
                {isGrace ? (
                  <span className="w-24 text-right text-sm font-semibold text-amber-600" data-testid={`adm-cal-grace-label-${i + 1}`}>0% · gracia</span>
                ) : (
                  <Input data-testid={`adm-cal-rate-${i + 1}`} type="text" inputMode="decimal" value={r.rate} onChange={(e) => setRate(i, e.target.value)} className="w-24 h-9 rounded-lg text-sm text-right" placeholder="—" />
                )}
                <label className="w-28 flex justify-center">
                  <input type="checkbox" data-testid={`adm-cal-gracechk-${i + 1}`} checked={isGrace} onChange={(e) => setGraceMonth(e.target.checked ? i + 1 : (graceMonth === i + 1 ? 1 : graceMonth))} />
                </label>
              </div>
            );
          })}
        </div>
      )}
      <div className="flex gap-2">
        <Button data-testid="adm-cal-copy" onClick={copyPrev} variant="outline" className="h-10 rounded-lg flex-1 text-xs">Copiar año anterior</Button>
        <Button data-testid="adm-cal-save" onClick={save} disabled={saving || !rows} className="h-10 rounded-lg flex-1 bg-zinc-900 hover:bg-zinc-800">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar calendario"}</Button>
      </div>
    </div>
  );
}

// ----------------------------- Carga histórica -----------------------------
function BulkLoad({ investorId, onDone }) {
  const [raw, setRaw] = useState("");
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const parse = () => {
    const rows = [];
    raw.split(/\r?\n/).map((l) => l.trim()).filter(Boolean).forEach((line) => {
      const c = line.split(";").map((x) => x.trim());
      rows.push({ type: c[0], amount: Number(c[1]), effective_date: c[2], period: c[3] || null, method: c[4] || null, reference: c[5] || null, note: c[6] || null, import_key: c[7] || null });
    });
    return rows;
  };
  const review = async () => {
    const rows = parse(); if (!rows.length) { toast.error("Pega al menos una fila"); return; }
    setSaving(true);
    try { const r = await adminApi.post(`/admin/investors/${investorId}/ledger/bulk`, { rows, confirm: false }); setPreview(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };
  const confirm = async () => {
    const rows = parse();
    setSaving(true);
    try { const r = await adminApi.post(`/admin/investors/${investorId}/ledger/bulk`, { rows, confirm: true }); toast.success(`${r.data.inserted} movimientos cargados`); setRaw(""); setPreview(null); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-4 space-y-3" data-testid="adm-bulk">
      <p className="text-sm font-semibold">Carga histórica en lote</p>
      <p className="text-[11px] text-zinc-500">Una fila por línea, separada por <b>;</b> en este orden: <code>tipo;monto;fecha;periodo;metodo;referencia;nota;import_key</code>. Ej: <code>APORTE_CAPITAL;10000;2025-03-01;;Zelle;REF123;Capital inicial;diego-1</code>. Tipos: APORTE_CAPITAL, RETIRO_CAPITAL, INTERES_GENERADO, PAGO_INTERES. Filas duplicadas (import_key o período de interés ya cargado) se omiten.</p>
      <textarea data-testid="adm-bulk-input" value={raw} onChange={(e) => setRaw(e.target.value)} rows={6} className="w-full rounded-xl border border-zinc-200 p-3 text-xs font-mono" placeholder="APORTE_CAPITAL;10000;2025-03-01;;Zelle;REF1;Capital inicial;k1" />
      {preview && (
        <div className="rounded-xl bg-zinc-50 border border-zinc-200 p-3 text-xs max-h-56 overflow-auto" data-testid="adm-bulk-preview">
          <p className="font-semibold mb-1">A insertar: {preview.to_insert} · duplicados: {preview.duplicates} · inválidos: {preview.invalid}</p>
          {preview.preview.map((r, i) => (
            <div key={i} className={`py-0.5 ${r.errors.length ? "text-rose-600" : r.duplicate ? "text-zinc-400" : ""}`}>
              {r.type} · {money(r.amount)} · {r.effective_date}{r.period ? ` · ${r.period}` : ""}{r.duplicate ? " (duplicado, se omite)" : ""}{r.errors.length ? ` — ${r.errors.join(", ")}` : ""}
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <Button data-testid="adm-bulk-review" onClick={review} disabled={saving} variant="outline" className="h-10 rounded-lg flex-1">Revisar</Button>
        <Button data-testid="adm-bulk-confirm" onClick={confirm} disabled={saving || !preview || preview.to_insert === 0} className="h-10 rounded-lg flex-1 bg-emerald-600 hover:bg-emerald-700">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirmar carga"}</Button>
      </div>
    </div>
  );
}

// ----------------------------- Ledger table -----------------------------
function LedgerTable({ investorId, reloadKey, onChanged, configLocked }) {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");
  const [editE, setEditE] = useState(null);
  const [adjustE, setAdjustE] = useState(null);
  const [histE, setHistE] = useState(null);
  const load = () => { const q = filter ? `?type=${filter}` : ""; adminApi.get(`/admin/investors/${investorId}/ledger${q}`).then((r) => setData(r.data)).catch(() => setData({ entries: [], balances: {} })); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [investorId, filter, reloadKey]);
  const del = async (id) => { if (!window.confirm("¿Eliminar este movimiento? Afecta los saldos derivados.")) return; try { await adminApi.delete(`/admin/ledger/${id}`); toast.success("Eliminado"); load(); onChanged?.(); } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } };
  if (!data) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-zinc-400" /></div>;
  return (
    <div className="space-y-2" data-testid="adm-ledger-table">
      {configLocked && <div className="rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 text-[11px] text-amber-700 flex items-center gap-1.5" data-testid="adm-ledger-locked-banner"><Lock className="w-3.5 h-3.5 shrink-0" /> Configuración histórica cerrada: las correcciones se registran como ajustes auditados (reversión + nueva entrada).</div>}
      <div className="flex gap-1.5 overflow-x-auto pb-1">
        {[["", "Todos"], ...Object.entries(TYPE_META).map(([k, v]) => [k, v.label])].map(([k, l]) => (
          <button key={k} data-testid={`adm-ledger-filter-${k || "all"}`} onClick={() => setFilter(k)} className={`shrink-0 h-8 px-3 rounded-full text-xs font-medium ${filter === k ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-600"}`}>{l}</button>
        ))}
      </div>
      <div className="rounded-2xl border border-zinc-200 bg-white divide-y divide-zinc-100">
        {data.entries.length === 0 ? <p className="text-sm text-zinc-400 py-8 text-center">Sin movimientos.</p> : data.entries.map((e) => {
          const M = TYPE_META[e.type] || {}; const Icon = M.Icon || Wallet;
          const sign = e.type === "RETIRO_CAPITAL" ? "−" : "";
          return (
            <div key={e.id} className="p-3 flex items-center justify-between gap-2" data-testid={`adm-ledger-row-${e.id}`}>
              <div className="flex items-center gap-2.5 min-w-0">
                <Icon className={`w-5 h-5 shrink-0 ${M.color}`} />
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{M.label || e.type}{e.period ? <span className="text-[11px] text-zinc-400 ml-1">· {e.period}</span> : ""}{e.adjustment ? <span className="text-[10px] text-violet-600 ml-1 font-semibold">AJUSTE</span> : ""}</p>
                  <p className="text-[11px] text-zinc-400 truncate">{fdate(e.effective_date)}{e.method ? ` · ${e.method}` : ""}{e.allocations ? ` · ${e.allocations.length} períodos` : ""}{e.reference ? ` · ${e.reference}` : ""}{e.note ? ` · ${e.note}` : ""}{e.historical ? " · histórico" : ""}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`text-sm font-semibold ${M.color}`}>{sign}{money(e.amount)}</span>
                {e.has_receipt && <button data-testid={`adm-ledger-receipt-${e.id}`} onClick={() => openReceiptAdmin(e.id)} className="text-emerald-700"><FileText className="w-4 h-4" /></button>}
                {configLocked ? (
                  <>
                    <button data-testid={`adm-ledger-adjust-${e.id}`} onClick={() => setAdjustE(e)} title="Ajuste auditado" className="text-zinc-400 hover:text-violet-700"><Scale className="w-3.5 h-3.5" /></button>
                    <button data-testid={`adm-ledger-history-${e.id}`} onClick={() => setHistE(e)} title="Historial de cambios" className="text-zinc-400 hover:text-zinc-700"><History className="w-3.5 h-3.5" /></button>
                  </>
                ) : (
                  <>
                    <button data-testid={`adm-ledger-edit-${e.id}`} onClick={() => setEditE(e)} className="text-zinc-400 hover:text-zinc-700"><Pencil className="w-3.5 h-3.5" /></button>
                    <button data-testid={`adm-ledger-del-${e.id}`} onClick={() => del(e.id)} className="text-zinc-400 hover:text-rose-600"><Trash2 className="w-3.5 h-3.5" /></button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {editE && <EditEntryDialog entry={editE} onClose={() => setEditE(null)} onDone={() => { setEditE(null); load(); onChanged?.(); }} />}
      {adjustE && <AdjustEntryDialog entry={adjustE} onClose={() => setAdjustE(null)} onDone={() => { setAdjustE(null); load(); onChanged?.(); }} />}
      {histE && <EntryHistoryDialog entry={histE} onClose={() => setHistE(null)} />}
    </div>
  );
}

function EditEntryDialog({ entry, onClose, onDone }) {
  const [f, setF] = useState({ type: entry.type, amount: entry.amount, effective_date: entry.effective_date, period: entry.period || "", method: entry.method || "", reference: entry.reference || "", note: entry.note || "" });
  const [saving, setSaving] = useState(false);
  // receiptAction: null = sin cambios, "new" = nuevo archivo (en receiptData), "delete" = eliminar
  const [receiptAction, setReceiptAction] = useState(null);
  const [receiptData, setReceiptData] = useState(null);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const pickFile = async (e) => { const file = e.target.files?.[0]; if (file) { setReceiptData(await fileToData(file)); setReceiptAction("new"); } };
  const save = async () => {
    const typeChanged = f.type !== entry.type;
    if (f.type === "INTERES_GENERADO" && !f.period) { toast.error("Interés generado requiere período (YYYY-MM)"); return; }
    if (typeChanged && !window.confirm("Cambiar el tipo de movimiento puede afectar los saldos derivados del inversionista. ¿Deseas continuar?")) return;
    setSaving(true);
    try {
      const payload = { amount: Number(f.amount), effective_date: f.effective_date, period: f.period || null, method: f.method || null, reference: f.reference || null, note: f.note || null };
      if (typeChanged) payload.type = f.type;
      if (receiptAction === "new") payload.receipt = receiptData;
      else if (receiptAction === "delete") payload.receipt = "";
      await adminApi.put(`/admin/ledger/${entry.id}`, payload);
      toast.success("Actualizado"); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };
  const hasExisting = entry.has_receipt && receiptAction !== "delete" && receiptAction !== "new";
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="adm-edit-dialog">
        <DialogHeader><DialogTitle>Editar movimiento</DialogTitle><DialogDescription>Puedes cambiar el tipo; afectará los saldos derivados.</DialogDescription></DialogHeader>
        <label className="text-xs text-zinc-500">Tipo</label>
        <select data-testid="adm-edit-type" value={f.type} onChange={(e) => set("type", e.target.value)} className="h-10 w-full rounded-lg border border-zinc-200 text-sm px-2 bg-white">
          {Object.entries(TYPE_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <label className="text-xs text-zinc-500">Monto</label><Input data-testid="adm-edit-amount" type="number" value={f.amount} onChange={(e) => set("amount", e.target.value)} className="h-10 rounded-lg" />
        <label className="text-xs text-zinc-500">Fecha efectiva</label><Input data-testid="adm-edit-date" type="date" value={f.effective_date} onChange={(e) => set("effective_date", e.target.value)} className="h-10 rounded-lg" />
        <label className="text-xs text-zinc-500">Período {f.type === "INTERES_GENERADO" ? "*" : "(opc.)"}</label><Input data-testid="adm-edit-period" placeholder="2026-02" value={f.period} onChange={(e) => set("period", e.target.value)} className="h-10 rounded-lg" />
        <label className="text-xs text-zinc-500">Nota</label><Input data-testid="adm-edit-note" value={f.note} onChange={(e) => set("note", e.target.value)} className="h-10 rounded-lg" />
        <div className="rounded-xl border border-zinc-200 p-2.5 mt-1" data-testid="adm-edit-receipt">
          <p className="text-xs text-zinc-500 mb-1.5">Comprobante (opcional)</p>
          {hasExisting ? (
            <div className="flex items-center gap-3 flex-wrap text-xs">
              <button type="button" data-testid="adm-edit-receipt-view" onClick={() => openReceiptAdmin(entry.id)} className="text-emerald-700 inline-flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> Ver</button>
              <label className="text-zinc-600 inline-flex items-center gap-1 cursor-pointer">Reemplazar<input type="file" accept="image/*,application/pdf" data-testid="adm-edit-receipt-replace" onChange={pickFile} className="hidden" /></label>
              <button type="button" data-testid="adm-edit-receipt-delete" onClick={() => { setReceiptAction("delete"); setReceiptData(null); }} className="text-rose-600 inline-flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Eliminar</button>
            </div>
          ) : receiptAction === "new" ? (
            <div className="flex items-center gap-3 text-xs"><span className="text-emerald-700">Nuevo comprobante listo</span><button type="button" onClick={() => { setReceiptAction(null); setReceiptData(null); }} className="text-zinc-500">Quitar</button></div>
          ) : receiptAction === "delete" ? (
            <div className="flex items-center gap-3 text-xs"><span className="text-rose-600">Se eliminará al guardar</span><button type="button" onClick={() => setReceiptAction(null)} className="text-zinc-500">Deshacer</button></div>
          ) : (
            <input type="file" accept="image/*,application/pdf" data-testid="adm-edit-receipt-upload" onChange={pickFile} className="text-xs" />
          )}
        </div>
        <Button data-testid="adm-edit-save" onClick={save} disabled={saving} className="h-10 rounded-lg bg-zinc-900 hover:bg-zinc-800 mt-1">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar"}</Button>
      </DialogContent>
    </Dialog>
  );
}

// ----------------------------- Ajuste auditado + historial -----------------------------
function AdjustEntryDialog({ entry, onClose, onDone }) {
  const [f, setF] = useState({ type: entry.type, amount: "", effective_date: today(), period: entry.period || "", method: "", note: "", reason: "" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    if (num(f.amount) == null || num(f.amount) <= 0) { toast.error("Monto > 0 requerido"); return; }
    if (!f.reason.trim()) { toast.error("El motivo del ajuste es obligatorio"); return; }
    if (f.type === "INTERES_GENERADO" && !f.period) { toast.error("Interés generado requiere período (YYYY-MM)"); return; }
    setSaving(true);
    try {
      await adminApi.post(`/admin/ledger/${entry.id}/adjust`, {
        type: f.type, amount: num(f.amount), effective_date: f.effective_date,
        period: f.period || null, method: f.method || null, note: f.note || null, reason: f.reason.trim(),
      });
      toast.success("Ajuste auditado registrado"); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="adm-adjust-dialog">
        <DialogHeader><DialogTitle>Ajuste auditado</DialogTitle><DialogDescription>Crea un NUEVO movimiento vinculado al original ({entry.id}) sin modificarlo. Para revertir, registra el tipo opuesto por el mismo monto.</DialogDescription></DialogHeader>
        <label className="text-xs text-zinc-500">Tipo</label>
        <select data-testid="adm-adjust-type" value={f.type} onChange={(e) => set("type", e.target.value)} className="h-10 w-full rounded-lg border border-zinc-200 text-sm px-2 bg-white">
          {Object.entries(TYPE_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <label className="text-xs text-zinc-500">Monto</label><Input data-testid="adm-adjust-amount" type="text" inputMode="decimal" placeholder="0,00" value={f.amount} onChange={(e) => set("amount", e.target.value)} className="h-10 rounded-lg" />
        <label className="text-xs text-zinc-500">Fecha efectiva</label><Input data-testid="adm-adjust-date" type="date" value={f.effective_date} onChange={(e) => set("effective_date", e.target.value)} className="h-10 rounded-lg" />
        <label className="text-xs text-zinc-500">Período {f.type === "INTERES_GENERADO" ? "*" : "(opc.)"}</label><Input data-testid="adm-adjust-period" placeholder="2026-02" value={f.period} onChange={(e) => set("period", e.target.value)} className="h-10 rounded-lg" />
        <label className="text-xs text-zinc-500">Motivo *</label><Input data-testid="adm-adjust-reason" value={f.reason} onChange={(e) => set("reason", e.target.value)} className="h-10 rounded-lg" placeholder="Ej. Reversión por error de monto" />
        <Button data-testid="adm-adjust-save" onClick={save} disabled={saving} className="h-10 rounded-lg bg-violet-600 hover:bg-violet-700 mt-1">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Registrar ajuste"}</Button>
      </DialogContent>
    </Dialog>
  );
}

function EntryHistoryDialog({ entry, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => { adminApi.get(`/admin/ledger/${entry.id}/history`).then((r) => setData(r.data)).catch(() => setData({ audit: [], adjustments: [] })); }, [entry.id]);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="adm-history-dialog">
        <DialogHeader><DialogTitle>Historial de cambios</DialogTitle><DialogDescription>Movimiento {entry.id} — auditoría de modificaciones y ajustes vinculados.</DialogDescription></DialogHeader>
        {!data ? <div className="flex justify-center py-6"><Loader2 className="w-4 h-4 animate-spin text-zinc-400" /></div> : (
          <div className="space-y-3 max-h-80 overflow-auto">
            <div>
              <p className="text-[11px] uppercase tracking-wider text-zinc-400 mb-1">Ajustes vinculados</p>
              {(data.adjustments || []).length === 0 ? <p className="text-xs text-zinc-400">Sin ajustes.</p> : data.adjustments.map((a) => (
                <div key={a.id} className="text-xs border border-violet-100 bg-violet-50/60 rounded-lg px-2.5 py-1.5 mb-1" data-testid={`adm-history-adj-${a.id}`}>
                  <b>{TYPE_META[a.type]?.label || a.type}</b> {money(a.amount)} · {fdate(a.effective_date)}{a.period ? ` · ${a.period}` : ""}<br /><span className="text-zinc-500">Motivo: {a.reason || "—"}</span>
                </div>
              ))}
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wider text-zinc-400 mb-1">Auditoría</p>
              {(data.audit || []).length === 0 ? <p className="text-xs text-zinc-400">Sin registros.</p> : data.audit.map((a, i) => (
                <div key={i} className="text-xs border border-zinc-100 rounded-lg px-2.5 py-1.5 mb-1" data-testid={`adm-history-audit-${i}`}>
                  <b>{a.action}</b> · {fdate(a.at)} · {a.user}{a.reason ? <><br /><span className="text-zinc-500">Motivo: {a.reason}</span></> : ""}
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ----------------------------- Historial financiero (tabla mensual) -----------------------------
function FinancialHistoryTab({ investorId, onChanged }) {
  const nowY = new Date().getFullYear();
  const YEARS = []; for (let y = nowY - 3; y <= nowY + 1; y++) YEARS.push(String(y));
  const [year, setYear] = useState(String(nowY));
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = (y) => adminApi.get(`/admin/investors/${investorId}/financial-history?year=${y}`).then((r) => setData(r.data)).catch(() => toast.error("No se pudo cargar"));
  useEffect(() => { setData(null); load(year); /* eslint-disable-next-line */ }, [investorId, year]);
  const locked = data?.config_locked;
  const toggle = async () => {
    const ep = locked ? "unlock" : "lock";
    if (!window.confirm(locked ? "¿Reabrir la configuración histórica? Volverás a poder editar/eliminar movimientos directamente." : "¿Finalizar la configuración histórica? Tras cerrarla, las correcciones deberán registrarse como ajustes auditados (reversión + nueva entrada), no editando los originales.")) return;
    setBusy(true);
    try { await adminApi.post(`/admin/investors/${investorId}/config/${ep}`); toast.success(locked ? "Configuración reabierta" : "Configuración histórica cerrada"); load(year); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="adm-financial-history">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-sm font-semibold">Historial financiero</p>
        <div className="flex items-center gap-1.5"><span className="text-xs text-zinc-500">Año</span>
          <select data-testid="adm-fh-year" value={year} onChange={(e) => setYear(e.target.value)} className="h-9 rounded-lg border border-zinc-200 text-sm px-2 bg-white">
            {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>
      <div className={`rounded-xl border px-3 py-2.5 flex items-center justify-between gap-2 flex-wrap ${locked ? "bg-amber-50 border-amber-200" : "bg-zinc-50 border-zinc-200"}`}>
        <p className="text-[11px] flex items-center gap-1.5" data-testid="adm-fh-lock-status">
          {locked ? <><Lock className="w-3.5 h-3.5 text-amber-600 shrink-0" /> <span className="text-amber-700">Configuración histórica <b>cerrada</b>. Correcciones solo por ajuste auditado.</span></> : <><Unlock className="w-3.5 h-3.5 text-zinc-500 shrink-0" /> <span className="text-zinc-600">Fase de configuración <b>abierta</b>. Puedes corregir el pasado libremente.</span></>}
        </p>
        <Button data-testid="adm-fh-lock-btn" onClick={toggle} disabled={busy || !data} variant="outline" className={`h-9 rounded-lg text-xs ${locked ? "text-zinc-700" : "text-amber-700 border-amber-300 hover:bg-amber-50"}`}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : locked ? "Reabrir configuración" : "Finalizar configuración histórica"}</Button>
      </div>
      {!data ? <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-zinc-400" /></div> : (
        <div className="rounded-2xl border border-zinc-200 bg-white divide-y divide-zinc-100" data-testid="adm-fh-table">
          <div className="grid grid-cols-[1fr_auto] gap-2 px-3 py-1.5 text-[10px] uppercase tracking-wider text-zinc-400"><span>Mes · Tasa · Interés</span><span>Estado</span></div>
          {data.months.map((m) => {
            const S = STATUS_META[m.status] || STATUS_META.SIN_INTERES;
            return (
              <div key={m.month} className="grid grid-cols-[1fr_auto] gap-2 px-3 py-2 items-center" data-testid={`adm-fh-row-${m.month}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium">{MONTHS_ES[m.month - 1]} <span className="text-[11px] text-zinc-400">· {m.grace ? "gracia 0%" : pct(m.rate_pct)}</span></p>
                  <p className="text-[11px] text-zinc-500">Generado {money(m.interest_generated)} · Pagado {money(m.interest_paid)} · Pend. {money(m.interest_pending)}</p>
                </div>
                <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full border ${S.cls}`} data-testid={`adm-fh-status-${m.month}`}>{S.label}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ----------------------------- Investor profile -----------------------------
function CreateAccessModal({ investor, onClose, onDone }) {
  const [email, setEmail] = useState(investor.email || "");
  const [pwd, setPwd] = useState(""); const [confirm, setConfirm] = useState(""); const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!email.trim()) { toast.error("Email requerido"); return; }
    if (pwd.length < 6) { toast.error("La contraseña debe tener al menos 6 caracteres"); return; }
    if (pwd !== confirm) { toast.error("Las contraseñas no coinciden"); return; }
    setSaving(true);
    try {
      await adminApi.post("/admin/users", { email: email.trim().toLowerCase(), name: investor.name, password: pwd, role: "inversionista", investor_id: investor.id });
      toast.success("Acceso al portal creado");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="adm-create-access-dialog">
        <DialogHeader><DialogTitle>Crear acceso al portal</DialogTitle><DialogDescription>Se crea el usuario de acceso del inversionista con cifrado seguro. La contraseña nunca se almacena en texto plano.</DialogDescription></DialogHeader>
        <label className="text-xs text-zinc-500">Email de acceso</label>
        <Input data-testid="adm-create-access-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-11 rounded-xl" />
        <label className="text-xs text-zinc-500">Contraseña</label>
        <Input data-testid="adm-create-access-pwd" type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} className="h-11 rounded-xl" />
        <label className="text-xs text-zinc-500">Confirmar contraseña</label>
        <Input data-testid="adm-create-access-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="h-11 rounded-xl" />
        <Button data-testid="adm-create-access-save" onClick={submit} disabled={saving} className="h-11 rounded-xl bg-zinc-900 hover:bg-zinc-800 mt-1">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Crear acceso"}</Button>
      </DialogContent>
    </Dialog>
  );
}

function ChangePwdModal({ email, onClose }) {
  const [pwd, setPwd] = useState(""); const [confirm, setConfirm] = useState(""); const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (pwd.length < 6) { toast.error("La contraseña debe tener al menos 6 caracteres"); return; }
    if (pwd !== confirm) { toast.error("Las contraseñas no coinciden"); return; }
    setSaving(true);
    try { await adminApi.put(`/admin/users/${encodeURIComponent(email)}`, { password: pwd }); toast.success("Contraseña actualizada"); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="adm-change-pwd-dialog">
        <DialogHeader><DialogTitle>Cambiar contraseña</DialogTitle><DialogDescription>No se muestra la contraseña actual. Se guarda con el mismo cifrado seguro del login.</DialogDescription></DialogHeader>
        <p className="text-xs text-zinc-400">Usuario: <b>{email}</b></p>
        <label className="text-xs text-zinc-500">Nueva contraseña</label>
        <Input data-testid="adm-change-pwd-new" type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} className="h-11 rounded-xl" />
        <label className="text-xs text-zinc-500">Confirmar contraseña</label>
        <Input data-testid="adm-change-pwd-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="h-11 rounded-xl" />
        <Button data-testid="adm-change-pwd-save" onClick={submit} disabled={saving} className="h-11 rounded-xl bg-zinc-900 hover:bg-zinc-800 mt-1">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar"}</Button>
      </DialogContent>
    </Dialog>
  );
}

function PortalAccessCard({ investor, accessUser, onChanged }) {
  const [createOpen, setCreateOpen] = useState(false);
  const [changeOpen, setChangeOpen] = useState(false);
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-4" data-testid="adm-portal-access">
      <p className="text-[11px] uppercase tracking-wider text-zinc-400 flex items-center gap-1.5"><KeyRound className="w-3.5 h-3.5" /> Acceso al portal</p>
      {accessUser ? (
        <div className="mt-1.5 flex items-center justify-between gap-2 flex-wrap">
          <div><p className="text-sm font-semibold text-emerald-700" data-testid="adm-access-status">Activo</p><p className="text-xs text-zinc-500" data-testid="adm-access-email">{accessUser}</p></div>
          <Button variant="outline" size="sm" data-testid="adm-change-pwd-btn" onClick={() => setChangeOpen(true)} className="h-9 rounded-lg">Cambiar contraseña</Button>
        </div>
      ) : (
        <div className="mt-1.5 flex items-center justify-between gap-2 flex-wrap">
          <p className="text-sm text-zinc-500" data-testid="adm-access-status">No configurado</p>
          <Button size="sm" data-testid="adm-create-access-btn" onClick={() => setCreateOpen(true)} className="h-9 rounded-lg bg-zinc-900 hover:bg-zinc-800">Crear acceso</Button>
        </div>
      )}
      {createOpen && <CreateAccessModal investor={investor} onClose={() => setCreateOpen(false)} onDone={() => { setCreateOpen(false); onChanged(); }} />}
      {changeOpen && <ChangePwdModal email={accessUser} onClose={() => setChangeOpen(false)} />}
    </div>
  );
}

function DeleteInvestorDialog({ investor, movementsCount, accessUser, onClose, onDeleted }) {
  const [saving, setSaving] = useState(false);
  const blocked = movementsCount > 0;
  const del = async () => {
    setSaving(true);
    try { await adminApi.delete(`/admin/investors/${investor.id}`); toast.success("Inversionista eliminado"); onDeleted(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="adm-delete-investor-dialog">
        <DialogHeader><DialogTitle>{blocked ? "No se puede eliminar" : `¿Eliminar a ${investor.name}?`}</DialogTitle>
          <DialogDescription>{blocked ? "No se puede eliminar este inversionista porque posee historial financiero. Sus movimientos quedan protegidos." : "Esta acción eliminará la ficha del inversionista."}</DialogDescription>
        </DialogHeader>
        {!blocked && accessUser && <p className="text-xs text-amber-600" data-testid="adm-delete-access-warn">También se eliminará el acceso al portal asociado ({accessUser}).</p>}
        {!blocked && (
          <Button data-testid="adm-delete-investor-confirm" onClick={del} disabled={saving} className="h-11 rounded-xl bg-rose-600 hover:bg-rose-700 mt-1">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Eliminar definitivamente"}</Button>
        )}
      </DialogContent>
    </Dialog>
  );
}

function YieldConfig({ investor, nextYield, onDone }) {
  const startDay = investor.agreement_start_date ? String(parseInt(investor.agreement_start_date.slice(8, 10), 10)) : "";
  const [f, setF] = useState({
    agreement_start_date: investor.agreement_start_date || "",
    yield_day: investor.yield_day != null ? String(investor.yield_day) : startDay,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const onStart = (v) => setF((s) => ({ ...s, agreement_start_date: v, yield_day: s.yield_day || (v ? String(parseInt(v.slice(8, 10), 10)) : "") }));
  const save = async () => {
    const yd = parseInt(f.yield_day, 10);
    if (f.yield_day && (isNaN(yd) || yd < 1 || yd > 31)) { toast.error("El día debe estar entre 1 y 31"); return; }
    setSaving(true);
    try {
      await adminApi.put(`/admin/investors/${investor.id}`, {
        agreement_start_date: f.agreement_start_date || null,
        yield_day: f.yield_day ? yd : null,
      });
      toast.success("Configuración de rendimientos guardada");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-4 space-y-3" data-testid="adm-yield-config">
      <p className="text-sm font-semibold flex items-center gap-1.5"><CalendarClock className="w-4 h-4 text-zinc-500" /> Configuración de rendimientos</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><label className="text-[11px] text-zinc-500">Fecha de inicio del acuerdo</label><Input data-testid="adm-yc-start" type="date" value={f.agreement_start_date} onChange={(e) => onStart(e.target.value)} className="h-10 rounded-lg" /></div>
        <div><label className="text-[11px] text-zinc-500">Día de generación mensual (1–31)</label><Input data-testid="adm-yc-day" type="text" inputMode="numeric" placeholder="15" value={f.yield_day} onChange={(e) => set("yield_day", e.target.value.replace(/[^0-9]/g, ""))} className="h-10 rounded-lg" /></div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="rounded-xl bg-zinc-50 border border-zinc-100 p-3" data-testid="adm-yc-nextdue">
          <p className="text-[11px] uppercase tracking-wider text-zinc-400">Próxima generación de intereses</p>
          <p className="text-sm font-medium mt-0.5">{nextYield?.next_due_date ? `${fdate(nextYield.next_due_date)} · período ${nextYield.period}` : "—"}</p>
        </div>
        <div className="rounded-xl bg-zinc-50 border border-zinc-100 p-3" data-testid="adm-yc-projected">
          <p className="text-[11px] uppercase tracking-wider text-zinc-400">Rendimiento proyectado</p>
          <p className="text-sm font-medium mt-0.5">{nextYield?.projected_interest != null ? `${money(nextYield.projected_interest)} (${money(nextYield.capital_base)} × ${pct(nextYield.rate_pct)})` : "—"}</p>
        </div>
      </div>
      <p className="text-[11px] text-zinc-400">Se sugiere el día según la fecha de inicio; puedes ajustarlo. Los aportes posteriores no cambian este día. Para 29–31 se usa el último día válido del mes. Cambiar el día no recalcula ni duplica rendimientos existentes.</p>
      <div className="flex justify-end"><Button data-testid="adm-yc-save" onClick={save} disabled={saving} className="h-10 rounded-lg bg-zinc-900 hover:bg-zinc-800">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar configuración"}</Button></div>
    </div>
  );
}

function InvestorProfile({ investorId, onBack }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("resumen");
  const [reloadKey, setReloadKey] = useState(0);
  const [delOpen, setDelOpen] = useState(false);
  const load = () => adminApi.get(`/admin/investors/${investorId}`).then((r) => setData(r.data)).catch(() => toast.error("No se pudo cargar"));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [investorId, reloadKey]);
  const refresh = () => setReloadKey((k) => k + 1);
  if (!data) return <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-zinc-400" /></div>;
  const b = data.balances; const ny = data.next_yield;
  const TABS = [["resumen", "Resumen"], ["historial", "Historial"], ["ledger", "Ledger"], ["registrar", "Registrar"], ["tasas", "Tasas"], ["historico", "Carga histórica"]];
  return (
    <div className="space-y-4" data-testid="adm-investor-profile">
      <button onClick={onBack} className="text-xs text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1"><ArrowLeft className="w-3.5 h-3.5" /> Volver</button>
      <div className="flex items-start justify-between gap-3">
        <div><h3 className="text-lg font-semibold flex items-center gap-2 flex-wrap">{data.investor.name}{data.investor.config_locked && <span className="text-[10px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 inline-flex items-center gap-1" data-testid="adm-prof-locked-chip"><Lock className="w-3 h-3" /> Histórico cerrado</span>}</h3><p className="text-xs text-zinc-400">{[data.investor.email, data.investor.phone].filter(Boolean).join(" · ") || "Sin datos de contacto"}</p></div>
        <Button variant="outline" size="sm" data-testid="adm-delete-investor-btn" onClick={() => setDelOpen(true)} className="h-9 rounded-lg shrink-0 text-rose-600 border-rose-200 hover:bg-rose-50"><Trash2 className="w-3.5 h-3.5 sm:mr-1.5" /><span className="hidden sm:inline">Eliminar</span></Button>
      </div>
      <PortalAccessManager kind="inversionista" refId={data.investor.id} name={data.investor.name} defaultEmail={data.investor.email} />
      {false && <PortalAccessCard investor={data.investor} accessUser={data.access_user} onChanged={refresh} />}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Stat label="Capital activo" value={money(b.active_capital)} accent testid="adm-prof-capital" />
        <Stat label="Intereses generados" value={money(b.interest_generated)} testid="adm-prof-generated" />
        <Stat label="Intereses pagados" value={money(b.interest_paid)} testid="adm-prof-paid" />
        <Stat label="Intereses pendientes" value={money(b.interest_pending)} testid="adm-prof-pending" />
      </div>
      <div className="flex gap-1.5 overflow-x-auto pb-1">
        {TABS.map(([k, l]) => <button key={k} data-testid={`adm-prof-tab-${k}`} onClick={() => setTab(k)} className={`shrink-0 h-9 px-3.5 rounded-full text-sm font-medium ${tab === k ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-600"}`}>{l}</button>)}
      </div>
      {tab === "resumen" && (
        <div className="space-y-3">
          <div className="rounded-2xl border border-zinc-200 bg-white p-4" data-testid="adm-prof-nextyield">
            <p className="text-[11px] uppercase tracking-wider text-zinc-400 flex items-center gap-1.5"><CalendarClock className="w-3.5 h-3.5" /> Próximo rendimiento</p>
            {ny?.rate_pct != null ? <p className="mt-1 text-sm">Período <b>{ny.period}</b>{ny.grace_zero ? ` (mes de gracia ${ny.candidate_period} = 0%)` : ""}: capital {money(ny.capital_base)} × {pct(ny.rate_pct)} ≈ <b>{money(ny.projected_interest)}</b>{ny.next_due_date ? <span className="text-zinc-400"> · vence {fdate(ny.next_due_date)}</span> : ""} <span className="text-zinc-400">(proyección, aún no generado)</span></p> : <p className="mt-1 text-sm text-zinc-400">Define una tasa para proyectar el próximo rendimiento.</p>}
          </div>
          <YieldConfig investor={data.investor} nextYield={ny} onDone={refresh} />
          <ChartsBlock capital={data.capital_series} interest={data.interest_series} />
        </div>
      )}
      {tab === "historial" && <FinancialHistoryTab investorId={investorId} onChanged={refresh} />}
      {tab === "ledger" && <LedgerTable investorId={investorId} reloadKey={reloadKey} onChanged={refresh} configLocked={data.investor.config_locked} />}
      {tab === "registrar" && (
        <div className="space-y-3">
          <AddMovementForm investorId={investorId} onDone={refresh} />
          <details className="rounded-2xl border border-amber-200 bg-amber-50/40" data-testid="adm-admin-tools">
            <summary className="cursor-pointer select-none px-4 py-3 text-sm font-semibold text-amber-800 flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> Herramientas administrativas (uso excepcional)</summary>
            <div className="px-4 pb-4 space-y-2">
              <p className="text-[11px] text-amber-700/90">Generación manual de rendimientos. Normalmente los intereses se generan automáticamente en el día configurado. Úsalo solo para correcciones o casos especiales.</p>
              <GenerateInterest investorId={investorId} nextYield={ny} onDone={refresh} />
            </div>
          </details>
        </div>
      )}
      {tab === "tasas" && <CalendarEditor investorId={investorId} onDone={refresh} />}
      {tab === "historico" && <BulkLoad investorId={investorId} onDone={refresh} />}
      {delOpen && <DeleteInvestorDialog investor={data.investor} movementsCount={data.movements_count} accessUser={data.access_user} onClose={() => setDelOpen(false)} onDeleted={() => { setDelOpen(false); onBack(); }} />}
    </div>
  );
}

export function ChartsBlock({ capital, interest }) {
  return (
    <div className="grid gap-3 lg:grid-cols-2">
      <div className="rounded-2xl border border-zinc-200 bg-white p-4" data-testid="chart-capital">
        <p className="text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-2">Evolución del capital</p>
        {(capital || []).length === 0 ? <p className="text-sm text-zinc-400 py-10 text-center">Sin datos.</p> : (
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={capital} margin={{ left: -10, right: 6, top: 6 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f1f3" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => (d || "").slice(0, 7)} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v) => money(v)} />
              <Line type="monotone" dataKey="capital" stroke="#059669" strokeWidth={2.5} dot={{ r: 2 }} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
      <div className="rounded-2xl border border-zinc-200 bg-white p-4" data-testid="chart-interest">
        <p className="text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-2">Intereses por período</p>
        {(interest || []).length === 0 ? <p className="text-sm text-zinc-400 py-10 text-center">Sin datos.</p> : (
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={interest} margin={{ left: -10, right: 6, top: 6 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f1f3" />
              <XAxis dataKey="period" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v) => money(v)} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="generated" name="Generado" fill="#2563eb" radius={[4, 4, 0, 0]} />
              <Bar dataKey="paid" name="Pagado" fill="#7c3aed" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

const WR_META = {
  PENDIENTE: { label: "Pendiente", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  PAGADA: { label: "Pagada", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  CANCELADA: { label: "Cancelada", cls: "bg-zinc-100 text-zinc-500 border-zinc-200" },
};

function PayRequestDialog({ req, onClose, onDone }) {
  const [f, setF] = useState({ effective_date: today(), method: "", note: "" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const pay = async () => {
    setSaving(true);
    try {
      await adminApi.post(`/admin/withdrawal-requests/${req.id}/pay`, { effective_date: f.effective_date, method: f.method || null, note: f.note || null });
      toast.success("Retiro registrado en el ledger");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="adm-wr-pay-dialog">
        <DialogHeader><DialogTitle>Marcar como pagada</DialogTitle><DialogDescription>Se creará UN movimiento RETIRO_CAPITAL de {money(req.amount)} para {req.investor_name || req.investor_id}. Esto reduce el capital activo y queda en el ledger.</DialogDescription></DialogHeader>
        <label className="text-xs text-zinc-500">Fecha efectiva</label>
        <Input data-testid="adm-wr-pay-date" type="date" value={f.effective_date} onChange={(e) => set("effective_date", e.target.value)} className="h-10 rounded-lg" />
        <label className="text-xs text-zinc-500">Método (opcional)</label>
        <select data-testid="adm-wr-pay-method" value={f.method} onChange={(e) => set("method", e.target.value)} className="h-10 w-full rounded-lg border border-zinc-200 text-sm px-2 bg-white">
          <option value="">— Opcional —</option>
          {["Efectivo", "Zelle", "USDT", "Transferencia", "Otro"].map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <label className="text-xs text-zinc-500">Nota (opcional)</label>
        <Input data-testid="adm-wr-pay-note" value={f.note} onChange={(e) => set("note", e.target.value)} className="h-10 rounded-lg" />
        <Button data-testid="adm-wr-pay-confirm" onClick={pay} disabled={saving} className="h-11 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white mt-1">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirmar pago y registrar retiro"}</Button>
      </DialogContent>
    </Dialog>
  );
}

function WithdrawalRequestsAdmin({ onChanged }) {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("");
  const [payReq, setPayReq] = useState(null);
  const load = () => { const q = filter ? `?status=${filter}` : ""; adminApi.get(`/admin/withdrawal-requests${q}`).then((r) => setRows(r.data)).catch(() => setRows([])); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);
  const cancel = async (o) => {
    if (!window.confirm(`¿Cancelar la solicitud ${o.id}? No se registrará ningún retiro.`)) return;
    try { await adminApi.post(`/admin/withdrawal-requests/${o.id}/cancel`); toast.success("Solicitud cancelada"); load(); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-4 space-y-3" data-testid="adm-withdrawal-requests">
      <p className="text-sm font-semibold flex items-center gap-1.5"><ArrowDownCircle className="w-4 h-4" /> Solicitudes de retiro</p>
      <div className="flex gap-1.5 overflow-x-auto pb-0.5">
        {[["", "Todas"], ["PENDIENTE", "Pendientes"], ["PAGADA", "Pagadas"], ["CANCELADA", "Canceladas"]].map(([k, l]) => (
          <button key={k || "all"} data-testid={`adm-wr-filter-${k || "all"}`} onClick={() => setFilter(k)} className={`shrink-0 h-8 px-3 rounded-full text-xs font-medium ${filter === k ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-600"}`}>{l}</button>
        ))}
      </div>
      {rows === null ? <div className="flex justify-center py-6"><Loader2 className="w-4 h-4 animate-spin text-zinc-400" /></div>
        : rows.length === 0 ? <p className="text-sm text-zinc-400 py-6 text-center">Sin solicitudes.</p>
          : (
            <div className="rounded-xl border border-zinc-200 divide-y divide-zinc-100 overflow-hidden">
              {rows.map((o) => {
                const m = WR_META[o.status] || WR_META.PENDIENTE;
                return (
                  <div key={o.id} className="flex items-center justify-between gap-2 px-3 py-2.5" data-testid={`adm-wr-row-${o.id}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-zinc-900 truncate">{o.investor_name || o.investor_id} · {money(o.amount)}</p>
                      <p className="text-[11px] text-zinc-500 truncate">{o.id} · {fdate(o.created_at)}{o.status === "PAGADA" && o.effective_date ? ` · pagada ${fdate(o.effective_date)}` : ""}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full border ${m.cls}`} data-testid={`adm-wr-status-${o.id}`}>{m.label}</span>
                      {o.status === "PENDIENTE" && (
                        <>
                          <button data-testid={`adm-wr-pay-${o.id}`} onClick={() => setPayReq(o)} className="h-8 px-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs">Pagar</button>
                          <button data-testid={`adm-wr-cancel-${o.id}`} onClick={() => cancel(o)} className="h-8 px-2.5 rounded-lg bg-zinc-100 hover:bg-zinc-200 text-zinc-600 text-xs">Cancelar</button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
      {payReq && <PayRequestDialog req={payReq} onClose={() => setPayReq(null)} onDone={() => { setPayReq(null); load(); onChanged?.(); }} />}
    </div>
  );
}

export function InvestmentsAdminTab() {
  const [dash, setDash] = useState(null);
  const [sel, setSel] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [nf, setNf] = useState({ name: "", email: "", phone: "" });
  const load = () => adminApi.get("/admin/investments-dashboard").then((r) => setDash(r.data)).catch(() => setDash({ totals: {}, per_investor: [] }));
  useEffect(() => { load(); }, []);
  const createInvestor = async () => {
    if (!nf.name.trim()) { toast.error("Nombre requerido"); return; }
    try { await adminApi.post("/admin/investors", { ...nf }); toast.success("Inversionista creado"); setNf({ name: "", email: "", phone: "" }); setNewOpen(false); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  if (sel) return <InvestorProfile investorId={sel} onBack={() => { setSel(null); load(); }} />;
  const t = dash?.totals || {};
  return (
    <div data-testid="investments-admin-panel" className="space-y-5">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Stat label="Capital activo total" value={money(t.active_capital)} accent testid="adm-dash-capital" />
        <Stat label="Intereses generados" value={money(t.interest_generated)} testid="adm-dash-generated" />
        <Stat label="Intereses pagados" value={money(t.interest_paid)} testid="adm-dash-paid" />
        <Stat label="Intereses pendientes" value={money(t.interest_pending)} testid="adm-dash-pending" />
      </div>
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold flex items-center gap-1.5"><Wallet className="w-4 h-4" /> Inversionistas</p>
        <Button size="sm" onClick={() => setNewOpen(true)} data-testid="adm-new-investor" className="h-9 rounded-lg bg-zinc-900 hover:bg-zinc-800 text-xs"><Plus className="w-3.5 h-3.5 mr-1" /> Nuevo inversionista</Button>
      </div>
      {dash === null ? <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-zinc-400" /></div> : (dash.per_investor || []).length === 0 ? <p className="text-sm text-zinc-400 py-8 text-center">No hay inversionistas.</p> : (
        <div className="grid gap-2 sm:grid-cols-2">
          {dash.per_investor.map((iv) => (
            <button key={iv.investor_id} data-testid={`adm-investor-${iv.investor_id}`} onClick={() => setSel(iv.investor_id)} className="text-left rounded-2xl border border-zinc-200 bg-white p-4 hover:border-zinc-300 transition-colors">
              <div className="flex items-center justify-between"><p className="font-semibold">{iv.name}</p>{!iv.active && <span className="text-[10px] text-rose-500">inactivo</span>}</div>
              <p className="text-lg font-bold mt-1 text-emerald-700">{money(iv.active_capital)} <span className="text-[11px] font-normal text-zinc-400">capital activo</span></p>
              <p className="text-[11px] text-zinc-400 mt-0.5">Pendiente {money(iv.interest_pending)} · próx. {iv.next_yield?.period} {iv.next_yield?.projected_interest != null ? `≈ ${money(iv.next_yield.projected_interest)}` : ""}</p>
            </button>
          ))}
        </div>
      )}
      <WithdrawalRequestsAdmin onChanged={load} />
      {newOpen && (
        <Dialog open onOpenChange={(o) => !o && setNewOpen(false)}>
          <DialogContent className="max-w-sm" data-testid="adm-new-investor-dialog">
            <DialogHeader><DialogTitle>Nuevo inversionista</DialogTitle><DialogDescription>Datos básicos de la ficha. Las tasas se configuran luego en la pestaña "Tasas", y el acceso al portal desde la ficha.</DialogDescription></DialogHeader>
            <Input data-testid="adm-investor-name" placeholder="Nombre *" value={nf.name} onChange={(e) => setNf({ ...nf, name: e.target.value })} className="h-11 rounded-xl" />
            <Input data-testid="adm-investor-email" placeholder="Email" value={nf.email} onChange={(e) => setNf({ ...nf, email: e.target.value })} className="h-11 rounded-xl" />
            <Input data-testid="adm-investor-phone" placeholder="Teléfono" value={nf.phone} onChange={(e) => setNf({ ...nf, phone: e.target.value })} className="h-11 rounded-xl" />
            <Button data-testid="adm-investor-save" onClick={createInvestor} className="h-11 rounded-xl bg-zinc-900 hover:bg-zinc-800 mt-1">Crear</Button>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
